import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/Button';
import { ListPanel } from '@/components/ListPanel';
import { ListSettings } from '@/components/ListSettings';
import { SavedViews } from '@/components/SavedViews';
import { Select, type SelectOption } from '@/components/Select';
import { PageHeader } from '@/components/PageHeader';
import { Table, type TableAction } from '@/components/Table';
import { Tabs, type TabItem } from '@/components/Tabs';
import { studyViewSchema, type StudyViewState } from '@/pages/Studies/listState';
import { useStudies } from '@/pages/Studies/useStudies';
import { useStudyColumns } from '@/pages/Studies/useStudyColumns';
import { StudyDialogs } from '@/pages/Studies/StudyDialogs';
import type { Tender } from '@/pages/Tenders/types';
type StudyTab = 'all' | 'in_study' | 'submitted' | 'not_submitted' | 'awarded' | 'not_awarded';
export const StudiesList = () => {
    const { t } = useTranslation('global');
    const model = useStudies();
    const { activeTab, studies, clients, kpis, loading, scope, search, setSearch, clientFilter, setClientFilter,
        yearFilter, setYearFilter, dueOnly, setDueOnly, sort, setSort, preferences, setPreferences,
        filtersOpen, setFiltersOpen, viewState, applyView, clearFilters, loadData, handleTabChange,
        setSelectedStudy, setStudyBc3Target, setBc3ModalOpen, handleOpenStatus, handleDownloadBc3, handleOpenEdit, handleOpenDelete, listing } = model;
    const availableYears = useMemo(() => {
        return listing.metadata?.years || [];
    }, [listing.metadata?.years]);

    const yearOptions: SelectOption<string>[] = useMemo(() => {
        return [
            { value: 'all', label: t('tender.filter_all_years') },
            ...availableYears.map((y) => ({ value: y, label: y })),
        ];
    }, [availableYears, t]);

    const clientOptions: SelectOption<string>[] = useMemo(() => {
        return [
            { value: 'all', label: t('tender.filter_all_clients') },
            ...clients.map((c) => ({ value: String(c.id), label: c.name })),
        ];
    }, [clients, t]);

    const filteredStudies = studies;
    const tabItems: TabItem<StudyTab>[] = [
        { id: 'all', label: t('studies.tab_all'), badge: kpis.total },
        { id: 'in_study', label: t('studies.tab_in_study'), badge: kpis.in_study },
        { id: 'submitted', label: t('studies.tab_submitted'), badge: kpis.submitted },
        { id: 'not_submitted', label: t('studies.tab_not_submitted'), badge: kpis.not_submitted },
        { id: 'awarded', label: t('studies.tab_awarded'), badge: kpis.awarded },
        { id: 'not_awarded', label: t('studies.tab_not_awarded'), badge: kpis.not_awarded },
    ];

    const columns = useStudyColumns(handleOpenStatus, setSelectedStudy);

    const tableActions: TableAction<Tender>[] = [
        {
            label: t('studies.action_view_detail'),
            icon: <Icon value="eye" size={14} className="text-slate-600 dark:text-zinc-300" />,
            onClick: (row: Tender): void => {
                setSelectedStudy(row);
            },
        },
        {
            label: t('common.edit'),
            icon: <Icon value="pencil" size={14} className="text-amber-600 dark:text-amber-400" />,
            onClick: (row: Tender): void => {
                handleOpenEdit(row);
            },
        },
        {
            label: t('studies.action_change_status'),
            icon: <Icon value="repeat" size={14} className="text-indigo-600 dark:text-indigo-400" />,
            onClick: (row: Tender): void => {
                handleOpenStatus(row);
            },
        },
        {
            label: t('studies.action_upload_bc3'),
            icon: <Icon value="fileText" size={14} className="text-blue-600 dark:text-blue-400" />,
            onClick: (row: Tender): void => {
                setStudyBc3Target(row);
                setBc3ModalOpen(true);
            },
        },
        {
            label: t('studies.action_download_bc3'),
            icon: <Icon value="download" size={14} className="text-emerald-600 dark:text-emerald-400" />,
            show: (row: Tender) => Boolean(row.bc3_file_path),
            onClick: (row: Tender): void => {
                void handleDownloadBc3(row);
            },
        },
        {
            label: t('common.archive'),
            icon: <Icon value="trash" size={14} className="text-red-600 dark:text-red-400" />,
            variant: 'delete',
            onClick: (row: Tender): void => {
                handleOpenDelete(row);
            },
        },
    ];

    const filterElement = <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" icon="filter" aria-expanded={filtersOpen} onClick={() => setFiltersOpen(previous => !previous)}>{t('providers.ui.filters')}</Button>
        <SavedViews<StudyViewState> storageKey={`vigepra_study_views:${scope}`} schema={studyViewSchema} value={viewState} onApply={applyView}
            defaultValue={{ tab: 'in_study', search: '', client: 'all', year: 'all', due: false, sort: { key: 'submission_date', order: 'desc' }, preferences: { density: 'comfortable', hidden: ['client_type_id'] }, pageSize: 20 }}
            configuration={<ListSettings inline columns={columns} value={preferences} onChange={setPreferences} sort={sort} onSortChange={setSort} />} />
    </div>;
    const dueCount = listing.metadata?.due_count || 0;

    return (
        <div className="space-y-3">
            <PageHeader compact className="!mb-0"
                title={t('studies.title')}
                action={<div className="flex items-center gap-2">
                    {activeTab === 'in_study' && dueCount > 0 && <Button variant="outline" size="sm" icon="calendar" aria-pressed={dueOnly} onClick={() => setDueOnly(previous => !previous)}>{t('workspace.list.deadline')}<span className="ml-2 font-bold">{dueCount}</span></Button>}
                    <Button variant="ghost" onlyicon icon="refresh" aria-label={t('workspace.list.refresh')} title={t('workspace.list.refresh')} disabled={loading} onClick={() => void loadData()} />
                </div>}
            />

            <Tabs<StudyTab>
                tabs={tabItems}
                activeTab={activeTab}
                onChange={handleTabChange}
                variant="underline"
                full={false}
                scroll={true}
                syncUrl={false}
                className="mb-3"
            />

            {/* Table */}
            <Table<Tender>
                data={filteredStudies}
                pagination={listing.pagination}
                manualSorting
                error={listing.error || model.record.error}
        onRetry={() => { listing.reload(); model.record.reload(); }}
                appearance="workspace"
                density={preferences.density}
                columns={columns.filter(column => !preferences.hidden.includes(column.key))}
                sort={sort}
                onSortChange={setSort}
                actionsPosition="right"
                actions={tableActions}
                searchable={true}
                filterbox={false}
                defaultSortKey="submission_date"
                defaultSortOrder="desc"
                searchValue={search}
                onSearchChange={(val: string) => setSearch(val)}
                searchPlaceholder={t('tender.search_placeholder')}
                filterElement={filterElement}
                filterFooter={<div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-zinc-400">
                    <span className="mr-auto">{t('workspace.list.results', { count: listing.total })}</span>
                    {clientFilter !== 'all' && <Button variant="outline" size="xs" icon="x" onClick={() => setClientFilter('all')}>{clients.find(client => String(client.id) === clientFilter)?.name}</Button>}
                    {yearFilter !== 'all' && <Button variant="outline" size="xs" icon="x" onClick={() => setYearFilter('all')}>{yearFilter}</Button>}
                    {dueOnly && <Button variant="outline" size="xs" icon="x" onClick={() => setDueOnly(false)}>{t('workspace.list.due_view')}</Button>}
                    {(search || clientFilter !== 'all' || yearFilter !== 'all' || dueOnly) && <Button variant="ghost" size="xs" onClick={clearFilters}>{t('workspace.list.clear')}</Button>}
                </div>}
                loading={loading}
                emptyText={search || clientFilter !== 'all' || yearFilter !== 'all' || dueOnly ? t('studies.empty_title_filtered') : t('studies.empty_title')}
                onRowClick={(row: Tender) => setSelectedStudy(row)}
                filterPanel={<ListPanel isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} title={t('providers.ui.filters')}
                    footer={<Button size="sm" variant="ghost" onClick={clearFilters}>{t('workspace.list.clear')}</Button>}>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <Select<string> label={t('tender.col_client')} value={clientFilter} onChange={value => setClientFilter(value || 'all')} options={clientOptions} searchable onSearchChange={model.clientLookup.search} />
                        <Select<string> label={t('workspace.list.year')} value={yearFilter} onChange={setYearFilter} options={yearOptions} />
                        <Select<string> label={t('tender.col_submission_date')} value={dueOnly ? 'due' : 'all'} onChange={value => setDueOnly(value === 'due')}
                            options={[{ value: 'all', label: t('workspace.list.all_dates') }, { value: 'due', label: t('workspace.list.due_view') }]} />
                    </div>
                </ListPanel>}
            />

            <StudyDialogs model={model} />
        </div>
    );
};
